/*
  Version 8.1/8.2 recommendations and catalog module. DOM-free: config, request URL,
  release-1 contract validation (ported verbatim from theme-dev/assets/coloring-blanket-v4.js),
  adaptation of API/catalog records into the Version 8 product shape, the local fallback
  choice, a fetch client with timeout, abort, stale-token guard and per-design cache, and
  (8.2) the full-catalog index, filters and search used by the browse paper.

  Browser: loaded before app.js, exposes window.DVIY_RECOMMENDATIONS_MODULE.
  Node: require()'d by the tests. No DOM access anywhere in this file.
*/
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module && module.exports) module.exports = api;
  if (root) root.DVIY_RECOMMENDATIONS_MODULE = Object.freeze(api);
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
  const HANDLE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  const PRODUCT_BASE_URL = 'https://davinciinyou.com/products/';
  const CALM_CATEGORIES = ['mandalas', 'flowers'];
  // Gus retired this accidental Mermaid duplicate on 2026-09-03. Keep the guard
  // here as well as removing it from the snapshot so stale API results cannot
  // reintroduce the product into recommendation rails.
  const RETIRED_DESIGN_CODES = new Set(['b2408150217']);

  const normalize = (value) => String(value || '').trim().toLowerCase();
  const clampLimit = (value) => Math.min(12, Math.max(1, Math.floor(Number(value) || 12)));

  // Search text: lowercase, "&" as "and", everything else non-alphanumeric as a space.
  const normalizeSearch = (value) =>
    String(value || '')
      .toLocaleLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  // Config is read once from window.DVIY_RECOMMENDATIONS. An empty apiBase means the
  // feature runs in fallback-only mode and never makes a request (contract section 1).
  function readConfig(source) {
    const raw = source && typeof source === 'object' ? source : {};
    const apiBase = String(raw.apiBase || '').trim().replace(/\/+$/, '');
    return Object.freeze({
      apiBase: apiBase || null,
      productType: String(raw.productType || '').trim() || 'coloring_blanket',
      limit: clampLimit(raw.limit),
      timeoutMs: Math.max(1, Number(raw.timeoutMs) || 4000),
    });
  }

  function buildRecommendationsUrl(config, sourceCode) {
    return (
      `${config.apiBase}/recommendations?page_id=${encodeURIComponent(sourceCode)}` +
      `&product_type=${encodeURIComponent(config.productType)}&limit=${config.limit}`
    );
  }

  // Contract section 4: exact keys and rules; any failure drops the whole record, and only
  // the contract keys are copied so nothing unexpected reaches the modal or cart state.
  function validateRecord(record) {
    if (!record || typeof record !== 'object' || Array.isArray(record)) return null;
    const text = (value, max) => typeof value === 'string' && value.length <= max;
    const digits = (value) => typeof value === 'string' && /^\d{1,20}$/.test(value);
    const cdnImage = (value) =>
      typeof value === 'string' && value.length <= 2048 && value.startsWith('https://cdn.shopify.com/');
    const cents = (value) => Number.isInteger(value) && value >= 0;
    if (!digits(record.id)) return null;
    if (!text(record.handle, 255) || !HANDLE_PATTERN.test(record.handle)) return null;
    if (!text(record.title, 300) || !record.title.trim()) return null;
    if (typeof record.designCode !== 'string' || !CODE_PATTERN.test(record.designCode)) return null;
    if (RETIRED_DESIGN_CODES.has(normalize(record.designCode))) return null;
    if (!cdnImage(record.image)) return null;
    if (!text(record.imageAlt, 300)) return null;
    if (record.coverImage !== null && !cdnImage(record.coverImage)) return null;
    if (!text(record.coverImageAlt, 300)) return null;
    if (!text(record.category, 64)) return null;
    if (!Array.isArray(record.sourceVariants) || !record.sourceVariants.length) return null;
    const sourceVariants = [];
    for (const variant of record.sourceVariants) {
      if (!variant || typeof variant !== 'object') return null;
      if (!digits(variant.sourceVariantId) || !text(variant.size, 64) || !cents(variant.price)) return null;
      if (variant.compareAtPrice !== null && !cents(variant.compareAtPrice)) return null;
      if (typeof variant.available !== 'boolean') return null;
      sourceVariants.push({
        sourceVariantId: variant.sourceVariantId,
        size: variant.size,
        price: variant.price,
        compareAtPrice: variant.compareAtPrice,
        available: variant.available,
      });
    }
    if (!sourceVariants.some((variant) => variant.available)) return null;
    return {
      id: record.id,
      handle: record.handle,
      title: record.title,
      designCode: record.designCode,
      image: record.image,
      imageAlt: record.imageAlt,
      coverImage: record.coverImage,
      coverImageAlt: record.coverImageAlt,
      category: record.category,
      sourceVariants,
    };
  }

  // Contract section 2 envelope + section 4 records. Invalid items are dropped one by one;
  // the result is unique by page_id and designCode, never contains the source, is sorted
  // by display_rank and capped at the limit. An empty result counts as a failure.
  function validatePayload(payload, sourceCode, limit) {
    if (!payload || typeof payload !== 'object' || payload.schema_version !== 1) return [];
    if (normalize(payload.source_page_id) !== normalize(sourceCode)) return [];
    if (!Array.isArray(payload.recommendations)) return [];
    const max = clampLimit(limit);
    const sourceKey = normalize(sourceCode);
    const seen = new Set();
    const items = [];
    payload.recommendations.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      if (typeof item.page_id !== 'string' || !CODE_PATTERN.test(item.page_id)) return;
      if (!Number.isInteger(item.display_rank) || item.display_rank < 1) return;
      const record = validateRecord(item.record);
      if (!record) return;
      const key = normalize(item.page_id);
      const codeKey = normalize(record.designCode);
      if (key === sourceKey || codeKey === sourceKey || seen.has(key) || seen.has(codeKey)) return;
      seen.add(key);
      seen.add(codeKey);
      items.push({ rank: item.display_rank, record });
    });
    items.sort((a, b) => a.rank - b.rank);
    return items.slice(0, max).map((item) => item.record);
  }

  // "Blanket Coloring Kit with 20 Fabric Markers - Puppy" -> "Puppy"; a plain title stays.
  function shortTitle(title) {
    const text = String(title || '').trim();
    const index = text.lastIndexOf(' - ');
    if (index <= 0) return text;
    const tail = text.slice(index + 3).trim();
    return tail || text;
  }

  function lowestAvailablePrice(variants) {
    const prices = (Array.isArray(variants) ? variants : [])
      .filter((variant) => variant && variant.available === true && Number.isInteger(variant.price))
      .map((variant) => variant.price);
    return prices.length ? Math.min(...prices) : null;
  }

  function indexByDesignCode(products) {
    const map = new Map();
    for (const product of Array.isArray(products) ? products : []) {
      const key = normalize(product && product.designCode);
      if (key && !map.has(key)) map.set(key, product);
    }
    return map;
  }

  // A record for one of the local designs resolves to that local product (five photos,
  // saved heart and reveal cue keep working). Anything else becomes a catalog product in
  // the Version 8 shape: cover photo first, then the line-art image; `lineArt` is the tile
  // image. The id is stable across visits: design-<code>.
  function adaptRecord(record, localByCode) {
    const local =
      localByCode && typeof localByCode.get === 'function' ? localByCode.get(normalize(record.designCode)) : null;
    if (local) return local;
    return {
      id: `design-${normalize(record.designCode)}`,
      designCode: record.designCode,
      shortTitle: shortTitle(record.title),
      title: record.title,
      url: `${PRODUCT_BASE_URL}${record.handle}`,
      images: [record.coverImage, record.image].filter(Boolean),
      lineArt: record.image,
      variants: record.sourceVariants.map((variant) => ({
        id: variant.sourceVariantId,
        size: variant.size,
        price: variant.price,
        compareAt: variant.compareAtPrice,
        available: variant.available,
      })),
      recommendationOnly: true,
    };
  }

  // SPEC section 5: local products in catalog order minus the source; designs sharing at
  // least one category with the source first, else all; capped at the limit.
  function localFallback(sourceProduct, products, categoriesById, limit) {
    const max = clampLimit(limit);
    const sourceId = sourceProduct ? sourceProduct.id : null;
    const candidates = (Array.isArray(products) ? products : []).filter((product) => product && product.id !== sourceId);
    const lookup = categoriesById && typeof categoriesById.get === 'function' ? categoriesById : null;
    const sourceCategories = lookup && sourceId ? lookup.get(sourceId) : null;
    if (sourceCategories && sourceCategories.size) {
      const shared = candidates.filter((product) => {
        const own = lookup.get(product.id);
        return own && [...own].some((category) => sourceCategories.has(category));
      });
      if (shared.length) return shared.slice(0, max);
    }
    return candidates.slice(0, max);
  }

  // 8.2 full catalog (a snapshot of theme-dev/assets/coloring-blanket-design-catalog.json).
  // One record per designCode in catalog order (a Sherpa record wins over a fleece twin),
  // validated with the contract rules (records with no available variant are dropped), and
  // adapted so the local designs keep their ids. Returns entries plus the category list.
  function buildCatalogIndex(catalogJson, localProducts) {
    const raw = catalogJson && Array.isArray(catalogJson.products) ? catalogJson.products : [];
    const localByCode = indexByDesignCode(localProducts);
    const isSherpa = (record) => /sherpa/.test(String((record && record.handle) || '').toLowerCase());
    const chosen = new Map();
    for (const record of raw) {
      const key = normalize(record && record.designCode);
      if (!key) continue;
      const current = chosen.get(key);
      if (!current || (!isSherpa(current) && isSherpa(record))) chosen.set(key, record);
    }
    const entries = [];
    const byId = new Map();
    const byCode = new Map();
    const categoryCounts = new Map();
    const categoryImage = new Map();
    let kidsCount = 0;
    let kidsImage = null;
    for (const rawRecord of chosen.values()) {
      const record = validateRecord(rawRecord);
      if (!record) continue;
      const product = adaptRecord(record, localByCode);
      const audience = String(rawRecord.audience || '').trim();
      const keywords = String(rawRecord.keywords || '').trim();
      const entry = {
        product,
        record,
        audience,
        keywords,
        searchable: normalizeSearch(`${record.title} ${record.category} ${keywords} ${record.designCode} ${audience}`),
      };
      entries.push(entry);
      byId.set(product.id, entry);
      byCode.set(normalize(record.designCode), entry);
      const category = record.category || 'More designs';
      categoryCounts.set(category, (categoryCounts.get(category) || 0) + 1);
      if (!categoryImage.has(category)) categoryImage.set(category, record.image);
      if (audience === 'Kids') {
        kidsCount += 1;
        if (!kidsImage) kidsImage = record.image;
      }
    }
    const categories = [...categoryCounts.entries()]
      .map(([label, count]) => ({ key: `category:${normalize(label)}`, label, count, image: categoryImage.get(label) }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    return { entries, byId, byCode, categories, kidsCount, kidsImage, availableCount: entries.length };
  }

  // Filters: all | saved (ids in savedIds) | kids (audience Kids) | calm (Mandalas or
  // Flowers, the "Calm, detailed designs" shelf) | category:<name>. Every query word must
  // appear in the entry's searchable text.
  function filterEntries(entries, filterKey, query, savedIds) {
    const words = normalizeSearch(query).split(' ').filter(Boolean);
    const key = String(filterKey || 'all');
    return (Array.isArray(entries) ? entries : []).filter((entry) => {
      if (key === 'saved' && !(savedIds && typeof savedIds.has === 'function' && savedIds.has(entry.product.id))) return false;
      if (key === 'kids' && entry.audience !== 'Kids') return false;
      if (key === 'calm' && !CALM_CATEGORIES.includes(normalize(entry.record.category))) return false;
      if (key.startsWith('category:') && `category:${normalize(entry.record.category)}` !== key) return false;
      return words.every((word) => entry.searchable.includes(word));
    });
  }

  // 8.2 live storefront product (Shopify's public /products/<handle>.js). Returns every image
  // (media of type image first, else the images list; protocol-relative URLs made absolute)
  // and the variants in the Version 8 shape, or null when the payload is unusable.
  function adaptStorefrontProduct(payload) {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
    const absolute = (value) => (typeof value === 'string' && value.startsWith('//') ? `https:${value}` : value);
    const cdn = (value) => typeof value === 'string' && value.length <= 2048 && value.startsWith('https://cdn.shopify.com/');
    const mediaImages = Array.isArray(payload.media)
      ? payload.media.filter((item) => item && item.media_type === 'image').map((item) => item.src)
      : [];
    const candidates = mediaImages.length ? mediaImages : Array.isArray(payload.images) ? payload.images : [];
    const images = [];
    const seen = new Set();
    for (const raw of candidates) {
      const src = absolute(raw);
      if (!cdn(src) || seen.has(src)) continue;
      seen.add(src);
      images.push(src);
    }
    const variants = [];
    for (const variant of Array.isArray(payload.variants) ? payload.variants : []) {
      if (!variant || typeof variant !== 'object') continue;
      const id = variant.id === null || variant.id === undefined ? '' : String(variant.id);
      if (!/^\d{1,20}$/.test(id) || !Number.isInteger(variant.price) || variant.price < 0) continue;
      // option1 is the size on its own; title appends other options ("50\" × 60\" / Beige").
      const size = String(variant.option1 || variant.title || '').trim();
      if (!size) continue;
      variants.push({
        id,
        size,
        price: variant.price,
        compareAt: Number.isInteger(variant.compare_at_price) && variant.compare_at_price >= 0 ? variant.compare_at_price : null,
        available: variant.available === true,
      });
    }
    if (!images.length || !variants.length) return null;
    return { images, variants };
  }

  function recommendationError(reason) {
    const error = new Error(`Recommendations unavailable: ${reason}.`);
    error.recsReason = reason;
    return error;
  }

  async function fetchJson(fetchImpl, url, signal) {
    const response = await fetchImpl(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
      signal,
    });
    if (!response || !response.ok) throw recommendationError(`http-${response ? response.status : 0}`);
    try {
      return await response.json();
    } catch (_error) {
      throw recommendationError('invalid-json');
    }
  }

  // One request per selection. The previous in-flight request is aborted, the timeout
  // aborts through the same controller, and an answer counts only while its token is
  // current. load() resolves with { status, reason, records } and never throws.
  function createClient(config, fetchImpl, timers) {
    const setTimer = timers && timers.setTimeout ? timers.setTimeout : setTimeout;
    const clearTimer = timers && timers.clearTimeout ? timers.clearTimeout : clearTimeout;
    const cache = new Map();
    let token = 0;
    let controller = null;

    const cancel = () => {
      token += 1;
      if (controller) controller.abort();
      controller = null;
    };

    async function load(sourceCode) {
      const code = String(sourceCode || '').trim();
      cancel();
      const current = token;
      if (!config.apiBase) return { status: 'fallback', reason: 'api-disabled', records: [] };
      if (!code) return { status: 'fallback', reason: 'no-source', records: [] };
      const key = normalize(code);
      if (cache.has(key)) return { status: 'api', reason: 'cache', records: cache.get(key) };
      const own = new AbortController();
      controller = own;
      const timer = setTimer(() => own.abort(), config.timeoutMs);
      try {
        const payload = await fetchJson(fetchImpl, buildRecommendationsUrl(config, code), own.signal);
        if (current !== token) return { status: 'stale', reason: 'superseded', records: [] };
        const records = validatePayload(payload, code, config.limit);
        if (!records.length) {
          const envelopeOk =
            payload && typeof payload === 'object' && payload.schema_version === 1 && Array.isArray(payload.recommendations) &&
            normalize(payload.source_page_id) === key;
          return { status: 'fallback', reason: envelopeOk ? 'no-valid-items' : 'invalid-payload', records: [] };
        }
        cache.set(key, records);
        return { status: 'api', reason: 'live', records };
      } catch (error) {
        if (current !== token) return { status: 'stale', reason: 'superseded', records: [] };
        const reason =
          error && error.recsReason ? error.recsReason : error && error.name === 'AbortError' ? 'timeout' : 'network';
        return { status: 'fallback', reason, records: [] };
      } finally {
        clearTimer(timer);
        if (controller === own) controller = null;
      }
    }

    return { load, cancel, cache };
  }

  return {
    PRODUCT_BASE_URL,
    normalize,
    normalizeSearch,
    readConfig,
    buildRecommendationsUrl,
    validateRecord,
    validatePayload,
    shortTitle,
    lowestAvailablePrice,
    adaptRecord,
    indexByDesignCode,
    localFallback,
    buildCatalogIndex,
    filterEntries,
    adaptStorefrontProduct,
    createClient,
  };
});
